import { getGlobalThat } from '@bcf-vanilla-ts-v1-shared/di/global-that';

type GlobalWithAppLang = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __APP_LANG__?: string;
};

export type AppLangResolution = {
  lang: string | undefined;
  redirectPathname: string | undefined;
};

export type InitializeAppLangSegmentOptions = {
  url: string | URL;
  baseHref: string;
  availableLangs: readonly string[];
  cookieHeader?: string;
  redirect: (pathname: string) => void;
  persist?: (lang: string) => void;
};

export type InitializeAppLangSegmentResult = {
  lang: string | undefined;
  redirected: boolean;
};

function normalizeBaseHref(baseHref: string): string {
  const pathname: string = new URL(baseHref, 'https://localhost').pathname;
  return pathname.endsWith('/') ? pathname : `${pathname}/`;
}

export function resolveAppLangSegment(
  url: string | URL,
  baseHref: string,
  availableLangs: readonly string[],
  cookieHeader?: string
): AppLangResolution {
  const pathname: string = typeof url === 'string' ? new URL(url, 'https://localhost').pathname : url.pathname;
  const normalizedBaseHref: string = normalizeBaseHref(baseHref);
  const pathnameWithoutBaseHref: string = pathname.startsWith(normalizedBaseHref)
    ? pathname.slice(normalizedBaseHref.length)
    : pathname;
  const lang: string | undefined = pathnameWithoutBaseHref.split('/').find((segment: string) => segment.length > 0);

  if (lang && availableLangs.includes(lang)) {
    return { lang: lang, redirectPathname: undefined };
  }
  if (lang) {
    return { lang: undefined, redirectPathname: normalizedBaseHref };
  }
  const cookieCountry: string | undefined = cookieHeader
    ?.split(';')
    .map((entry: string) => entry.trim().split('='))
    .find(([name]: string[]) => name === 'bcfCountry')?.[1]
    ?.toLowerCase();
  const fallbackLang: string | undefined =
    availableLangs.find((candidate: string) => candidate === cookieCountry) ?? availableLangs[0];
  if (!fallbackLang) {
    throw new Error('Cannot initialize app language without an available language');
  }
  return { lang: undefined, redirectPathname: `${normalizedBaseHref}${fallbackLang}/` };
}

export function setAppLangSegment(lang: string): void {
  const globalAny: GlobalWithAppLang = getGlobalThat();
  globalAny['__APP_LANG__'] = lang;
}

export function getAppLangSegment(): string {
  const globalAny: GlobalWithAppLang = getGlobalThat();
  return globalAny['__APP_LANG__'] as string;
}

export function initializeAppLangSegment(options: InitializeAppLangSegmentOptions): InitializeAppLangSegmentResult {
  const resolution: AppLangResolution = resolveAppLangSegment(
    options.url,
    options.baseHref,
    options.availableLangs,
    options.cookieHeader
  );
  if (resolution.redirectPathname) {
    options.redirect(resolution.redirectPathname);
    return { lang: undefined, redirected: true };
  }

  const lang: string = resolution.lang as string;
  setAppLangSegment(lang);
  options.persist?.(lang);
  return { lang: lang, redirected: false };
}

export function resetAppLangSegment(): void {
  const globalAny: GlobalWithAppLang = getGlobalThat();
  delete globalAny['__APP_LANG__'];
}
